import { memo, useCallback } from 'react';
import { CartContainer, EntityList } from '@aimsos/ui';

import { typeInitials } from '../utils/typeInitials.js';

import { Link2 } from 'lucide-react';

/**
 * One record in the entity list.
 *
 * Renders through `CartContainer` + `EntityList` for every entity type — no
 * per-type branch, which is the rule the whole domain exists to prove.
 * Confirmed against Michael's Entity List Figma (CT-5270 open question #11):
 * this is the correct pair, not `EntityListCard` — an earlier pass here used
 * `EntityListCard` instead, which was wrong and has been reverted.
 *
 * The normalized fields map onto `EntityList`'s own slots:
 *
 *   title           -> title
 *   source + code   -> primaryMeta (renders inline after title, dot-separated)
 *   badges[0]       -> status (single badge, top-right — the design has one)
 *   description     -> description (component owns its own expand/collapse)
 *   nextBestAction  -> aiInsight (the violet "Next Best Action" row is this
 *                      component's own built-in pattern, not a custom box)
 *   meta            -> secondaryMeta (footer-left)
 *   typeTag         -> tags (footer-right)
 *   visual          -> avatar (initials fallback; an unreachable `src` would
 *                      render broken, so `src` is only passed when present)
 *
 * A field the tenant's mapping did not fill is nullish, not `''`, so the slot
 * simply goes unused and the row renders without empty chrome.
 */
const EntityRowCard = memo(function EntityRowCard({ entity, typeLabel, onSelect }) {
  // Reports the whole record, not just its id: under the All view the caller
  // needs the row's own type to know whose profile to open.
  const handleClick = useCallback(() => {
    onSelect?.(entity);
  }, [onSelect, entity]);

  const primaryMeta = [];
  if (entity.source) primaryMeta.push({ icon: Link2, label: entity.source });
  if (entity.code) primaryMeta.push({ label: `#${entity.code}` });

  // The payload's own tag wins; `typeLabel` is only the fallback for a row that
  // arrives untagged in a list that mixes types.
  const tagLabel = entity.typeTag ?? typeLabel;

  const status = entity.badges?.[0] ? { label: entity.badges[0].label, variant: entity.badges[0].variant } : undefined;

  const aiInsight = entity.nextBestAction
    ? {
        label: 'Next Best Action',
        text: `${entity.nextBestAction.label} · ${entity.nextBestAction.timestamp}`,
      }
    : undefined;

  // `EntityList.secondaryMeta` only renders `label` (or `text`) — unlike
  // `EntityListCard`'s footer items, it has no separate `value` slot. Fold
  // the mapper's {label, value} pairs into one string so `{ label: 'Region',
  // value: 'AMER' }` still reads as "Region: AMER" instead of silently
  // dropping the value.
  const secondaryMeta = entity.meta?.map((item) => ({
    icon: item.icon,
    label: item.value != null ? `${item.label}: ${item.value}` : (item.label ?? item.text),
  }));

  return (
    <div data-testid="entity-row" data-entity-id={entity.id}>
      {/* size="S" (12px padding) matches the "UCP Contacts" prototype's actual
          row density — CartContainer's default `L` (24px) rendered every row
          ~1.5x taller than the reference. */}
      <CartContainer size="S" variant="neutral" onClick={handleClick}>
        <EntityList
          title={entity.title ?? undefined}
          avatar={{
            src: entity.visual || undefined,
            initials: typeInitials(entity.title ?? ''),
            alt: entity.title ?? undefined,
          }}
          primaryMeta={primaryMeta}
          status={status}
          description={entity.description ?? undefined}
          aiInsight={aiInsight}
          secondaryMeta={secondaryMeta}
          tags={tagLabel ? [{ label: tagLabel }] : undefined}
        />
      </CartContainer>
    </div>
  );
});

export default EntityRowCard;
